// Most of this from https://github.com/tcampb/react-calendly

export interface ICalendlyPrefill {
    name: string;
    email: string;
    firstName: string;
    lastName: string;
    smsReminderNumber: string;
    location: string;
    guests: string[];
    customAnswers?: Partial<{
        a1: string;
        a2: string;
        a3: string;
        a4: string;
        a5: string;
        a6: string;
        a7: string;
        a8: string;
        a9: string;
        a10: string;
    }>;
    date: Date;
}

export enum CalendlyEvent {
    EVENT_SCHEDULED = "calendly.event_scheduled",
    PAGE_HEIGHT = "calendly.page_height",
}

export const CALENDLY_EVENT_NAME = "message";

export type CalendlyEventScheduledEvent = MessageEvent<{
    event: CalendlyEvent.EVENT_SCHEDULED;
    payload: {
        event: {
            /**
             * @description Canonical reference (unique identifier) to the event that was scheduled.
             * @example https://calendly.com/api/v2/scheduled_events/AAAAAAAAAAAAAA
             * @see {@link https://developer.calendly.com/docs/api-docs/reference/calendly-api/openapi.yaml/paths/~1scheduled_events~1%7Buuid%7D/get} for further information.
             */
            uri: string;
        };
        invitee: {
            /**
             * @description Canonical reference (unique identifier) for the invitee who scheduled the event.
             * @example https://calendly.com/api/v2/scheduled_events/AAAAAAAAAAAAAA/invitees/AAAAAAAAAAAAAA
             * @see {@link https://developer.calendly.com/docs/api-docs/reference/calendly-api/openapi.yaml/paths/~1scheduled_events~1%7Bevent_uuid%7D~1invitees~1%7Binvitee_uuid%7D/get} for further information.
             */
            uri: string;
        };
    };
}>;

export type CalendlyPageHeightEvent = MessageEvent<{
    event: CalendlyEvent.PAGE_HEIGHT;
    payload: {
        height: string;
    };
}>;

export function isCalendlyEvent(e: MessageEvent): e is CalendlyEventScheduledEvent | CalendlyPageHeightEvent {
    return e.origin === "https://calendly.com"
        && e.data.event
        && Object.values(CalendlyEvent).includes(e.data.event);
}

export interface ICalendlyPageSettings {
    /**
     * @description Use this setting to hide your profile picture, name, event duration, location, and description when Calendly is embedded. This will help reduce duplicate information that you may already have on your web page.
     * @see {@link https://help.calendly.com/hc/en-us/articles/360020052833-Advanced-embed-options#2} for further information.
     */
    hideLandingPageDetails: boolean;

    /**
     * @description Use this setting to hide your profile picture, name, event duration, location, and description when Calendly is embedded. This will help reduce duplicate information that you may already have on your web page.
     * @see {@link https://help.calendly.com/hc/en-us/articles/360020052833-Advanced-embed-options#2} for further information.
     */
    hideEventTypeDetails: boolean;

    /**
     * @description This setting is only available for Calendly users on the Pro plan. Use this setting to change your Calendly scheduling page's background color.
     * @example 00a2ff
     * @see {@link https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview#3} for further information.
     */
    backgroundColor: string;

    /**
     * @description This setting is only available for Calendly users on the Pro plan. Use this setting to change your Calendly scheduling page's text color.
     * @example ffffff
     * @see {@link https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview#3} for further information.
     */
    textColor: string;

    /**
     * @description This setting is only available for Calendly users on the Pro plan. Use this setting to change your Calendly scheduling page's primary color.
     * @example 4d5055
     * @see {@link https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview#3} for further information.
     */
    primaryColor: string;

    /**
     * @description The General Data Protection Regulation governs data protection in the EU and EEA. Certain Calendly integrations require access to cookies with user information. If you do not embed the GDPR banner, users in those areas will not have the ability to give their consent in order to access integrations such as Google Analytics, Facebook Pixel, PayPal, and Stripe.
     * @see {@link https://help.calendly.com/hc/en-us/articles/360007385493-Cookie-FAQs} for further information.
     */
    hideGdprBanner: boolean;
}

export function formatCalendlyUrl({
    url,
    prefill = {},
    pageSettings = {},
    embedType,
}: {
    url: string;
    prefill?: Partial<ICalendlyPrefill>;
    pageSettings?: Partial<ICalendlyPageSettings>;
    embedType?: "Inline" | "PopupWidget" | "PopupButton";
}) {
    const {
        backgroundColor,
        hideEventTypeDetails,
        hideLandingPageDetails,
        primaryColor,
        textColor,
        hideGdprBanner,
    } = pageSettings;

    const {
        customAnswers,
        date,
        email,
        firstName,
        guests,
        lastName,
        location,
        smsReminderNumber,
        name,
    } = prefill;

    const queryStringIndex = url.indexOf("?");
    const hasQueryString = queryStringIndex > -1;
    const queryString = url.slice(queryStringIndex + 1);
    const baseUrl = hasQueryString ? url.slice(0, queryStringIndex) : url;

    const updatedQueryString = [
        hasQueryString ? queryString : null,
        backgroundColor ? `background_color=${backgroundColor}` : null,
        hideEventTypeDetails ? `hide_event_type_details=1` : null,
        hideLandingPageDetails ? `hide_landing_page_details=1` : null,
        primaryColor ? `primary_color=${primaryColor}` : null,
        textColor ? `text_color=${textColor}` : null,
        hideGdprBanner ? `hide_gdpr_banner=1` : null,
        name ? `name=${encodeURIComponent(name)}` : null,
        smsReminderNumber ? `phone_number=${encodeURIComponent(smsReminderNumber)}` : null,
        location ? `location=${encodeURIComponent(location)}` : null,
        firstName ? `first_name=${encodeURIComponent(firstName)}` : null,
        lastName ? `last_name=${encodeURIComponent(lastName)}` : null,
        guests ? `guests=${guests.map(encodeURIComponent).join(",")}` : null,
        email ? `email=${encodeURIComponent(email)}` : null,
        date && date instanceof Date ? `date=${formatDate(date)}` : null,
        embedType ? `embed_type=${embedType}` : null,
        /*
         * https://github.com/tcampb/react-calendly/pull/31
         * embed_domain must be defined to receive messages from the Calendly iframe.
         */
        `embed_domain=1`,
    ]
        .concat(customAnswers ? formatCustomAnswers(customAnswers) : [])
        .filter((item) => item !== null)
        .join("&");

    return `${baseUrl}?${updatedQueryString}`;
}

function formatDate(d: Date) {
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();

    return [
        year,
        month < 10 ? `0${month}` : month,
        day < 10 ? `0${day}` : day,
    ].join("-");
}

const CUSTOM_ANSWER_PATTERN = /^a\d{1,2}$/;

function formatCustomAnswers(customAnswers: Record<string, string>) {
    const customAnswersFiltered = Object.keys(customAnswers).filter((key) =>
        key.match(CUSTOM_ANSWER_PATTERN),
    );

    if (!customAnswersFiltered.length) return [];

    return customAnswersFiltered.map(
        (key) => `${key}=${encodeURIComponent(customAnswers[key])}`,
    );
}
